import ButtonLink from "../ButtonLink";
import "./PropertyDetails.css";
import PropertyCard, { PropertyCardProps } from "../../listings/PropertyCard";
import { Badge } from "../../shadcn/Badge";
import { ContactInfo, Listing } from "src/types/listing";
import { formatPhoneNumber } from "../PropertySummary";
import PhoneTypeIcon from "../PhoneTypeIcon";
import ContactCard from "../ContactCard";
import { LayoutType } from "../ContactCard";
import ScrollContainer from "src/components/core/ScrollContainer";
import { Pencil, Copy, Check } from "lucide-react";
import { useState } from "react";
import OwnerInformation from "src/components/listingDetails/OwnerInformation";
import copyTextToClipboard from "../copyToCLipBoard";

export type PropertyDetailsProps = {
  listing: Listing;
  isNotesEnabled?: boolean;
  isShowPreview?: boolean;
  isTagsEnabled?: boolean;
  isSuppressPreview?: boolean;
  isSuppressListingStats?: boolean;
  onAddNoteClick?: () => void;
  onAddTagClick?: () => void;
  isSuppressContactCards?: boolean;
  onFavoriteClick?: () => void;
  isShowStarredIconWithTitle?: boolean;
  isLoading?: boolean;
  PropertyCardProps?: PropertyCardProps;
  customSection1Content?: React.ReactNode;
  onToggleContactIsDisabled?: (
    contact: ContactInfo,
    isDisabled: boolean
  ) => void;
  isContactEditingDisabled?: boolean;
};

const LandvoiceIdSection = ({ id }: { id: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await copyTextToClipboard(id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex flex-col gap-1 items-start text-sm">
      <div className="section-title">Landvoice ID</div>
      <div className="flex items-center gap-2">
        <span className="text-gray-secondary">{id}</span>
        <button
          onClick={handleCopy}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          title={copied ? "Copied!" : "Copy ID"}
        >
          {copied ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
        </button>
      </div>
    </div>
  );
};

export const PropertyDetails = ({
  listing,
  isNotesEnabled = false,
  isTagsEnabled = false,
  isSuppressPreview = false,
  isSuppressListingStats = false,
  onAddNoteClick,
  onAddTagClick,
  isSuppressContactCards = false,
  onFavoriteClick,
  isShowStarredIconWithTitle = false,
  isLoading = false,
  PropertyCardProps,
  customSection1Content = null,
  onToggleContactIsDisabled,
  isContactEditingDisabled = false,
}: PropertyDetailsProps) => {
  return (
    <div className="flex flex-col gap-4 mb-4">
      <PropertyCard
        isLoading={isLoading}
        listing={listing}
        suppressGutters
        isSuppressPreview={isSuppressPreview}
        isSuppressListingStats={isSuppressListingStats}
        onFavoriteClick={onFavoriteClick}
        isShowStarredIconWithTitle={isShowStarredIconWithTitle}
        {...PropertyCardProps}
      />
      {!isLoading && listing && (
        <>
          {customSection1Content && <div>{customSection1Content}</div>}
          <div className="flex flex-col gap-2 items-start">
            {!!listing?.contacts?.length && (
              <div className="section-title">Contacts</div>
            )}
            <>
              {!isSuppressContactCards &&
                listing?.contacts?.map((contact, index) => (
                  <ContactCard
                    className="w-full"
                    layoutType={LayoutType.horizontal}
                    contact={contact}
                    key={`contact-card-${contact.id}`}
                    isEditingDisabled={isContactEditingDisabled}
                    onToggleIsDisabled={(isDisabled) =>
                      onToggleContactIsDisabled?.(contact, isDisabled)
                    }
                  />
                ))}
            </>
            {isSuppressContactCards && (
              <div className="w-full flex flex-col gap-2">
                {(() => {
                  const shownNames = new Set();
                  return listing?.contacts?.map((contact) => (
                    <div
                      className="grid grid-cols-2 gap-5"
                      key={`contact-${contact.id}`}
                    >
                      <div className="body-text">
                        {!shownNames.has(contact.fullName) &&
                          shownNames.add(contact.fullName) &&
                          contact.fullName}
                      </div>
                      <div
                        className={`body-text flex flex-row gap-2 items-center ${
                          contact.dnc ? "!text-red-700" : ""
                        }`}
                      >
                        <div className="min-w-[12px]">
                          <PhoneTypeIcon
                            type={contact.phoneType}
                            dnc={contact.dnc}
                          />
                        </div>
                        {`${formatPhoneNumber(contact?.phone)}`}
                        {contact.dnc && (
                          <div className="border-red-700 font-bold border-[1.5px] rounded-sm px-1">
                            DNC
                          </div>
                        )}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}
          </div>
          {isNotesEnabled && (
            <div className="flex flex-col gap-2 items-start text-sm w-full">
              <div className="section-title flex items-center gap-2">
                Notes
                <Pencil
                  size={16}
                  className="cursor-pointer text-gray-500 hover:text-gray-700"
                  onClick={onAddNoteClick}
                />
              </div>
              {!!listing?.notes?.length && (
                <ScrollContainer className="w-full">
                  <div className="note-container flex flex-col gap-2 max-h-[200px] w-full">
                    {listing.notes.map((note, index) => (
                      <pre
                        key={note.id}
                        style={{
                          fontFamily: "Lato",
                        }}
                        className="note text-[13px] text-gray-secondary border-gray-200 gap-2 border-b py-1"
                      >
                        {note.note}
                      </pre>
                    ))}
                  </div>
                </ScrollContainer>
              )}
              {!listing?.notes?.length && (
                <ButtonLink onClick={onAddNoteClick} className="link-button">
                  Click to add notes
                </ButtonLink>
              )}
            </div>
          )}
          {isTagsEnabled && (
            <div className="flex flex-col gap-2 items-start text-sm text-gray-600">
              <div className="section-title flex items-center gap-2">
                Tags
                <Pencil
                  size={16}
                  className="cursor-pointer text-gray-500 hover:text-gray-700"
                  onClick={onAddTagClick}
                />
              </div>
              {!!listing?.tags?.length && (
                <ScrollContainer className="w-full">
                  <div className="flex flex-wrap gap-2 mt-2 max-h-[100px] w-full">
                    {listing.tags.map((tag, index) => (
                      <Badge
                        key={`tag ${index}`}
                        className="text-gray-primary"
                        variant="outline"
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </ScrollContainer>
              )}
              {!listing?.tags?.length && (
                <ButtonLink onClick={onAddTagClick} className="link-button">
                  Click to add tags
                </ButtonLink>
              )}
            </div>
          )}
          <OwnerInformation owner={listing?.ownerContactInfo} />
          {listing?.id && (
            <LandvoiceIdSection id={listing.id} />
          )}
        </>
      )}
    </div>
  );
};
